import { ROUTE_CONFIG } from "./config";

import type { ProductFeatureKey } from "@signalist/shared-types";

export type RouteKey = keyof typeof ROUTE_CONFIG;

/**
 * The product feature every web route belongs to (PREC-4329, usage analytics 2/10), read by the
 * web tracking of PREC-4333; nothing reads it at runtime yet.
 *
 * A `Record` over the keys of `ROUTE_CONFIG`, checked with `satisfies`: a route added to
 * `ROUTE_CONFIG` without an entry here, or an entry for a route that is gone, fails
 * `pnpm --filter @signalist/web typecheck`. `null` is an explicit "not a feature":
 * - sign-in, sign-up, password, e-mail confirmation, legal and error pages, and the CRM OAuth
 *   callbacks (the provider's page maps the connection);
 * - keys no route in `Private.tsx` or `Public.tsx` mounts any more, which render NotFound.
 * Staff-only pages map to features of the Admin area, so staff use stays visible and a dashboard
 * can leave the area out.
 */
export const ROUTE_FEATURES = {
	LOGIN: null,
	REGISTER: null,
	TFA: null,
	LOGOUT: null,
	FORGOT_PASSWORD: null,
	ERROR_PAGE: null,
	ACCOUNT: "account-settings",
	CRON: "admin-operations",
	ADMIN_FIXES: "admin-operations",
	API_HEALTH: "admin-operations",
	COMPANY: "workspace-settings",
	BLACKLIST: null,
	BLACKLIST_GLOBAL: "admin-global-blacklist",
	PITCHDECKS: null,
	LISTS: "list-builder",
	NEW_LIST: "list-builder",
	LISTS_DETAIL: "list-table",
	PROFILE_TRACKER: "profile-tracker",
	FEED: "feed",
	USERS: "user-management",
	USER_ROLES: "user-management",
	JOBS: null,
	INDEED: null,
	NORTHDATA: null,
	COMPANIES: "admin-companies",
	CSV_ENRICHMENTS: null,
	CUSTOM_EVENTS: "admin-custom-events",
	LINKEDIN_EVENTS: null,
	LINKEDIN_COMPANY_ACTIVITY: null,
	LINKEDIN_POST: null,
	LINKEDIN_PROFILE_VISITS: null,
	LINKEDIN_PROFILE: null,
	LINKEDIN_PEOPLE: null,
	WORKFLOWS: null,
	ACCOUNTS: null,
	OPENAI: null,
	DASHBOARD: "dashboard",
	INBOX: null,
	SCORE: null,
	MESSAGE_CENTER: "ready-to-send",
	MESSAGE_CENTER_COMPOSE: "compose",
	MESSAGE_CENTER_INBOX: "inbox",
	MESSAGE_CENTER_OUTBOX: "outbox",
	MESSAGE_CENTER_FOLLOW_UP: null,
	MAPS: null,
	CAMPAIGNS: "campaigns-overview",
	REPLIES: null,
	NO_REPLY: null,
	TO_BE_NURTURED: null,
	DO_NOT_CONTACT: "blacklist",
	DO_NOT_CONTACT_COMPETITORS: "competitors",
	DO_NOT_CONTACT_BLACKLIST: "blacklist",
	DO_NOT_CONTACT_SUPPRESSED: "blacklist",
	ONBOARDING: "onboarding",
	ONBOARDING_CHAT: "onboarding",
	ONBOARDING_LISTS: "onboarding",
	ONBOARDING_TRACK: "onboarding",
	ONBOARDING_PRIME: "onboarding",
	ONBOARDING_DONE: "onboarding",
	ONBOARDING_SUBSCRIPTION: "billing",
	SELECT_SUBSCRIPTION: "billing",
	SELECT_SUBSCRIPTION_DONE: "billing",
	ONBOARDING_V3: "onboarding",
	LEAD_SCORING: "lead-scoring-setup",
	LEAD_SCORING_GENERAL: "lead-scoring-setup",
	LEAD_SCORING_COMPANY: "lead-scoring-setup",
	LEAD_SCORING_PROSPECT: "lead-scoring-setup",
	LEAD_SCORING_OVERVIEW: "lead-scoring-overview",
	LEAD_SCORING_CRITERIA: "lead-scoring-setup",
	LEAD_SCORING_JOB_TITLES: "job-title-screening",
	LEAD_SCORING_LOADING: "lead-scoring-setup",
	CONFIRM_EMAIL: null,
	VERIFY_EMAIL: null,
	ANALYTICS: "analytics",
	APPLICATION_STATUS: null,
	TEMPLATES: "message-templates",
	COPYWRITING: "copywriting-settings",
	SIGNALS: "signals",
	KNOWLEDGE_DB: "knowledge-base",
	HELP_CENTER_ADMIN: "admin-help-conversations",
	SUPPORT: "support-tickets",
	SUPPORT_DASHBOARD: "admin-support",
	TICKETS: "admin-support",
	FEATURE_USAGE: "admin-feature-management",
	FEATURE_MANAGEMENT: "admin-feature-management",
	CONTACTS: "contacts",
	CONTACTS_COMPANIES: "contact-companies",
	HUBSPOT_CALLBACK: null,
	ZOHO_CALLBACK: null,
	PIPEDRIVE_CALLBACK: null,
	MSDYNAMICS_CALLBACK: null,
	SALESFORCE_CALLBACK: null,
	PRIVACY_POLICY: null,
	INTEGRATIONS: "integrations-overview",
	INTEGRATIONS_HUBSPOT: "crm-hubspot",
	INTEGRATIONS_ZOHO: "crm-zoho",
	INTEGRATIONS_PIPEDRIVE: "crm-pipedrive",
	INTEGRATIONS_MSDYNAMICS: "crm-msdynamics",
	INTEGRATIONS_SALESFORCE: "crm-salesforce",
	INTEGRATIONS_LEMLIST: "lemlist",
	INTEGRATIONS_MCP: "mcp-connector",
	INTEGRATIONS_N8N: "n8n",
	INTEGRATIONS_API: "public-api",
	INTEGRATIONS_WEBHOOKS: "webhooks",
	INTEGRATIONS_WEBHOOK_DELIVERIES: "webhooks",
	MCP_CONSENT: "mcp-connector",
} as const satisfies Record<RouteKey, ProductFeatureKey | null>;
